import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  ApiError,
  Spinner,
  apiFetch,
  type PortalDashboard,
  type PortalSignableDocument,
} from "@dearbill/shared";
import { MemberLayout } from "./MemberLayout";
import { PortalSign } from "./PortalSign";
import { useEnrollmentDocuments } from "./hooks/useEnrollmentDocuments";
import { selectPlanYear, type PlanYearSelection } from "./planYear";
import { DocumentSign } from "./sign/DocumentSign";
import { M3P_DOCUMENT_ID, documentSignPath } from "./sign/documents";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

interface MemberDashboardProps {
  session: string;
  /** Clears the session: the layout's Sign out button and a rejected (401) session both call it. */
  onSignOut: () => void;
  /** Set on a `/forms/:enrollmentId/sign/:documentSlug` link: show that document's sign page. */
  documentSign?: { enrollmentId: string; slug: string } | undefined;
}

/** What the signed-in screens read from the outlet with `useOutletContext`. */
export interface DashboardOutletContext extends PlanYearSelection {
  data: PortalDashboard;
  /** Picks `years.current` or `years.next`; Home's and Cost Estimate's toggles share it. */
  setPlanYear: (year: number) => void;
  /** Opens the sign flow; set only while the latest enrollment is signable. */
  onSign?: (() => void) | undefined;
  /** The latest enrollment's signable documents, CMS-10882 excluded. */
  documents: PortalSignableDocument[];
  /** Opens a document's sign page. */
  onSignDocument: (doc: PortalSignableDocument) => void;
  /** Refetches the dashboard; `data` stays on screen until the new payload lands. */
  reload: () => void;
  /** Portal session bearer, for a screen's own requests (My Medications' add, Settings' `/me` read). */
  session: string;
  /** Clears the session; a screen's own request rejected with 401 calls it. */
  onSignOut: () => void;
}

/**
 * The signed-in member portal: loads `GET /api/portal/dashboard` once and
 * renders the current screen (Home, My Programs, My Medications, Cost
 * Estimate, Account, Settings) through the outlet inside `MemberLayout`, so
 * switching screens issues no second request; a screen that writes (My
 * Medications' add) calls the context's `reload` to refetch, and Settings
 * makes its own `GET /api/portal/me` read. Loading, the load error, the screens
 * and the sign flow all render within the one layout, so the chrome stays
 * mounted between them. A 401 means the session is dead and calls `onSignOut`.
 *
 * The plan-year pick lives here, so Home's and Cost Estimate's toggles move
 * together and a refetch keeps it; `selectPlanYear` resolves it against each
 * payload at render.
 *
 * While `latestEnrollment.signable`, Home's Action Needed button swaps the
 * content for `PortalSign`, driven by `latestEnrollment.id`. The sign view is
 * tied to the history entry it opened on, so navigating to another screen
 * unmounts it and a nav click back to Home shows Home. Leaving the sign view
 * (success, a gate bounce, or a 409/422) returns here and refetches, so a
 * completed signature removes the panel.
 *
 * Home also lists every other signable document on the latest enrollment,
 * from `GET .../documents` (passed through the outlet context), and offers a
 * sign row for each the session can sign. That row navigates to the
 * document's sign route, where `documentSign` swaps the content for
 * `DocumentSign` once the dashboard has loaded; leaving it navigates back to
 * `/`.
 */
export function MemberDashboard({
  session,
  onSignOut,
  documentSign,
}: MemberDashboardProps): ReactNode {
  const navigate = useNavigate();
  const { key: locationKey } = useLocation();
  const [data, setData] = useState<PortalDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signingKey, setSigningKey] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Null until the member uses a toggle; the payload decides the default.
  const [pickedYear, setPickedYear] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<PortalDashboard>("/api/portal/dashboard", {}, { baseUrl: API_BASE, token: session })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onSignOut();
          return;
        }
        setError("We couldn't load your dashboard. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [session, onSignOut, reloadKey]);

  const allDocuments = useEnrollmentDocuments(
    documentSign ? null : (data?.latestEnrollment?.id ?? null),
    session,
    onSignOut,
  );
  const documents = allDocuments.filter((d) => d.documentId !== M3P_DOCUMENT_ID);

  const exitDocumentSign = useCallback((): void => {
    void navigate("/", { replace: true });
  }, [navigate]);

  const exitSign = useCallback((): void => {
    setSigningKey(null);
    setData(null);
    setError(null);
    setReloadKey((k) => k + 1);
  }, []);

  let content: ReactNode;
  // The sign page waits for the dashboard, whose `role` decides whether an
  // unsignable document names the signer who may sign it.
  if (documentSign && data) {
    content = (
      <div className="w-full max-w-2xl">
        <DocumentSign
          // A new link remounts the page, so no state carries across documents.
          key={`${documentSign.enrollmentId}/${documentSign.slug}`}
          session={session}
          role={data.role}
          enrollmentId={documentSign.enrollmentId}
          slug={documentSign.slug}
          onSignOut={onSignOut}
          onExit={exitDocumentSign}
        />
      </div>
    );
  } else if (signingKey === locationKey && data?.latestEnrollment) {
    content = (
      <div className="w-full max-w-2xl">
        <PortalSign
          session={session}
          enrollmentId={data.latestEnrollment.id}
          onSignOut={onSignOut}
          onExit={exitSign}
        />
      </div>
    );
  } else if (error) {
    content = <Alert tone="error" title={error} className="max-w-2xl" />;
  } else if (!data) {
    content = (
      <div className="text-slate-teal flex items-center gap-3">
        <Spinner /> <span>Loading your dashboard…</span>
      </div>
    );
  } else {
    content = (
      <Outlet
        context={
          {
            data,
            ...selectPlanYear(data, pickedYear),
            setPlanYear: setPickedYear,
            onSign: data.latestEnrollment?.signable ? () => setSigningKey(locationKey) : undefined,
            documents,
            onSignDocument: (doc) => {
              const id = data.latestEnrollment?.id;
              if (id) void navigate(documentSignPath(id, doc.documentId));
            },
            reload: () => setReloadKey((k) => k + 1),
            session,
            onSignOut,
          } satisfies DashboardOutletContext
        }
      />
    );
  }

  return (
    <MemberLayout session={session} onSignOut={onSignOut}>
      {content}
    </MemberLayout>
  );
}
